import React from "react";
import { BlockMath, InlineMath, ReadableInlineMath } from "../../../components/math/KatexWrappers";
import EquationInput from "../../../components/inputs/EquationInput";
import FractionInput from "../../../components/inputs/FractionInput";
import { scoreStaged } from "../../openTaskScoring";

const CASES = [
  { name: "Kamil", amount: 300, firstItem: "koszulkę", secondItem: "torbę sportową", first: [1, 5], second: [3, 5] },
  { name: "Ola", amount: 240, firstItem: "bluzę", secondItem: "buty", first: [1, 4], second: [2, 5] },
  { name: "Maja", amount: 180, firstItem: "książkę", secondItem: "plecak", first: [1, 3], second: [1, 2] },
  { name: "Tomek", amount: 360, firstItem: "kask", secondItem: "rękawiczki", first: [1, 6], second: [3, 4] },
  { name: "Basia", amount: 420, firstItem: "rakietę", secondItem: "strój sportowy", first: [2, 7], second: [1, 3] },
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function fracLatex([num, den]) {
  return `\\frac{${num}}{${den}}`;
}

function buildData(config) {
  const firstSpent = (config.amount * config.first[0]) / config.first[1];
  const afterFirst = config.amount - firstSpent;
  const secondSpent = (afterFirst * config.second[0]) / config.second[1];
  const remaining = afterFirst - secondSpent;
  return { ...config, firstSpent, afterFirst, secondSpent, remaining, maxPoints: 3 };
}

function createOriginalTask() {
  return {
    type: "E8ShoppingFractionRemainingOpenTask",
    data: buildData(CASES[0]),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 17 },
    tags: ["e8", "ulamki-zwykle", "zadanie-tekstowe", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8ShoppingFractionRemainingOpenTask",
    data: buildData(pick(CASES.slice(1))),
    source: { variant: "random", basedOn: "E8_2025_CZE_17" },
    tags: ["e8", "ulamki-zwykle", "zadanie-tekstowe", "zadanie-otwarte"],
  };
}

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  return String(value);
}

function parseNumberLike(value) {
  const raw = rawValue(value).trim().replace(",", ".").replace(/\s+/g, "");
  if (!raw) return null;
  if (raw.includes("/")) {
    const [a, b] = raw.split("/").map(Number);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return null;
    return a / b;
  }
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}

function normalize(raw) {
  return rawValue(raw).toLowerCase().replace(/,/g, ".").replace(/\s+/g, "");
}

function hasNumber(raw, target, tolerance = 0.01) {
  const numbers = normalize(raw).match(/\d+(?:\.\d+)?/g) || [];
  return numbers.some((value) => Math.abs(Number(value) - target) <= tolerance);
}

function firstStepOk(raw, data) {
  const text = normalize(raw);
  return text && hasNumber(text, data.firstSpent) && hasNumber(text, data.afterFirst);
}

function secondStepOk(raw, data) {
  const text = normalize(raw);
  return text && hasNumber(text, data.secondSpent) && hasNumber(text, data.remaining);
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        {data.name} otrzymał <InlineMath math={`${data.amount}`} /> złotych na zakup {data.firstItem} i {data.secondItem}.
        Na {data.firstItem} wydał <ReadableInlineMath math={fracLatex(data.first)} scale={1.05} /> tej kwoty.
        Za {data.secondItem} zapłacił <ReadableInlineMath math={fracLatex(data.second)} scale={1.05} /> kwoty, która została mu po zakupie {data.firstItem}.
      </p>
      <p>Oblicz, ile złotych zostało {data.name.endsWith("a") ? "jej" : "mu"} po zakupach. Zapisz obliczenia.</p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => onChange({ ...(userAnswer || {}), [field]: value });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 1. Oblicz, ile zostało po pierwszym zakupie.</div>
        <EquationInput value={userAnswer.afterFirst || ""} onChange={setField("afterFirst")} disabled={disabled} placeholder="np. 300-1/5*300=240" showPreview basis="420px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 2. Oblicz, ile zostało po drugim zakupie.</div>
        <EquationInput value={userAnswer.afterSecond || ""} onChange={setField("afterSecond")} disabled={disabled} placeholder="np. 240-3/5*240=96" showPreview basis="420px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 3. Wpisz końcową kwotę.</div>
        <FractionInput label="z" suffixLatex="\\text{zł}" value={userAnswer.result} onChange={setField("result")} disabled={disabled} placeholder="np. 96" />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const step1 = firstStepOk(userAnswer.afterFirst, data);
  const step2 = secondStepOk(userAnswer.afterSecond, data);
  const result = parseNumberLike(userAnswer.result);
  const resultOk = result != null && Math.abs(result - data.remaining) <= 0.01;
  return scoreStaged({
    resultOk,
    milestones: [step1, step2],
    maxScore: data.maxPoints,
    parts: { "po pierwszym zakupie": step1, "po drugim zakupie": step2, "wynik": resultOk },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`${fracLatex(data.first)}\\cdot ${data.amount}=${data.firstSpent}`} />
      <BlockMath math={`${data.amount}-${data.firstSpent}=${data.afterFirst}`} />
      <BlockMath math={`${fracLatex(data.second)}\\cdot ${data.afterFirst}=${data.secondSpent}`} />
      <BlockMath math={`${data.afterFirst}-${data.secondSpent}=${data.remaining}`} />
      <div>{"Odpowied\u017a: po zakupach zosta\u0142o "}<InlineMath math={`${data.remaining}\\ \\text{z\u0142}`} />{"."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const result = parseNumberLike(userAnswer.result);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>Po pierwszym zakupie: {userAnswer.afterFirst || <i>(brak)</i>}</div>
      <div>Po drugim zakupie: {userAnswer.afterSecond || <i>(brak)</i>}</div>
      <div>Wynik: {result == null ? <i>(brak)</i> : <InlineMath math={`${result}\\ \\text{zł}`} />}</div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Obliczamy koszt pierwszego zakupu i kwotę, która została.</p>
      <BlockMath math={`${fracLatex(data.first)}\\cdot ${data.amount}=${data.firstSpent}`} />
      <BlockMath math={`${data.amount}-${data.firstSpent}=${data.afterFirst}`} />
      <p><strong>Krok 2.</strong> Drugi zakup liczymy od kwoty, która została po pierwszym zakupie.</p>
      <BlockMath math={`${fracLatex(data.second)}\\cdot ${data.afterFirst}=${data.secondSpent}`} />
      <BlockMath math={`${data.afterFirst}-${data.secondSpent}=${data.remaining}`} />
      <p>Po zakupach zostało <InlineMath math={`${data.remaining}\\ \\text{zł}`} />.</p>
    </div>
  );
}